import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';
import { useAuthStore } from '@/features/auth/auth.store';

/**
 * Requirements, sides and add-ons — the three kinds of modifier a prepared item
 * can carry.
 *
 * They are one API with three nouns in it: same list shape, same
 * `createorupdate`, same per-option delete, differing only in the word in the
 * path and in which fields an option has. So they are one module, and the pages
 * differ by a `kind` rather than by being copies of each other.
 *
 * Scope: these live either on the business or globally, decided by the login
 * state's `globalReqSideAddons`. When they are per-business the store's id
 * prefixes the list URL and rides along in the save body — the same value in
 * two shapes, which is why both are derived in one place here.
 */

export type ModifierKind = 'requirement' | 'side' | 'addon';

/** Plural for the list URL, singular everywhere else. */
const PLURAL: Record<ModifierKind, string> = {
  requirement: 'requirements',
  side: 'sides',
  addon: 'addons',
};

/** One choice inside a modifier group. */
export interface ModifierOption {
  id: string;
  name: string | null;
  description: string | null;
  imageUrl: string | null;
  ordering: number | null;
  isAvailable: boolean;
  allowVariableAmount: boolean;
  /** Sides only, and both come from the catalogue item that was picked. */
  brand?: string | null;
  itemId?: string | null;
  /** Add-ons only. */
  price?: number | null;
  isAlcoholicBeverage?: boolean;
}

/** A modifier group: the question, and how many answers it takes. */
export interface ModifierGroup {
  id: string;
  internalName: string | null;
  name: string | null;
  minRequired: number | null;
  maxRequired: number | null;
  /** When on, a variable-amount option counts its amount towards min/max. */
  amountOptionsCountForMinMax: boolean;
  items: ModifierOption[] | null;
}

/** New options carry a client-side id until the group is saved. */
export const TEMP_PREFIX = 'TEMP-';
export const isTempId = (id: string) => id.startsWith(TEMP_PREFIX);
export const newTempId = () =>
  `${TEMP_PREFIX}${Math.random().toString(36).slice(2, 10)}`;

/**
 * Where these records live for this client.
 *
 * `globalReqSideAddons` is on the login state in the original. Until that blob
 * is ported it reads as global, which is the shape MECA uses; the store id is
 * still threaded through so switching is one value.
 */
function useScope() {
  const business = useAuthStore((s) => s.business);
  const isGlobal = true;
  return {
    isGlobal,
    businessId: isGlobal ? null : (business?.id ?? null),
  };
}

export function useModifierGroups(kind: ModifierKind) {
  const { businessId } = useScope();
  const path = businessId
    ? `${businessId}/deli/${PLURAL[kind]}`
    : `deli/${PLURAL[kind]}`;

  return useQuery({
    queryKey: ['modifiers', kind, businessId],
    queryFn: () =>
      unwrap<{ results: ModifierGroup[] }>(
        api.get<ApiEnvelope<{ results: ModifierGroup[] }>>(path)
      ),
    select: (data) => data.results ?? [],
  });
}

function useInvalidate(kind: ModifierKind) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['modifiers', kind] });
}

/**
 * Save the group and every option in one call.
 *
 * Options are edited in memory and only reach the API here, which is why a new
 * one carries a `TEMP-` id: the ordering is the array's own, and the temporary
 * ids are cleared on the way out so the server assigns real ones.
 */
export function useSaveModifierGroup(kind: ModifierKind) {
  const invalidate = useInvalidate(kind);
  const { businessId } = useScope();

  return useMutation({
    mutationFn: (group: ModifierGroup) => {
      const body = {
        [kind]: {
          ...group,
          id: group.id && !isTempId(group.id) ? group.id : null,
          items: (group.items ?? []).map((option, index) => ({
            ...option,
            ordering: index,
            id: isTempId(option.id) ? null : option.id,
          })),
        },
        businessId,
      };
      return unwrap<unknown>(api.post(`deli/${kind}/createorupdate`, body));
    },
    onSuccess: invalidate,
  });
}

export function useDeleteModifierGroup(kind: ModifierKind) {
  const invalidate = useInvalidate(kind);

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.delete(`deli/${kind}/${id}`)),
    onSuccess: invalidate,
  });
}

/**
 * Remove one saved option.
 *
 * A POST, not a DELETE, and the noun is glued together — `requirementoption`,
 * `sideoption`, `addonoption`. An option that was never saved is dropped from
 * the array without calling this at all.
 */
export function useDeleteModifierOption(kind: ModifierKind) {
  return useMutation({
    mutationFn: (optionId: string) =>
      unwrap<unknown>(api.post(`deli/delete/${kind}option/${optionId}`, {})),
  });
}

/** Option images have their own uploader, separate from the catalogue's. */
export function useUploadOptionImage() {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post('businesses/reqoptionsimages/upload/file', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}

// ------------------------------------------------------------ item picker

/** `businesses/catalog/search` — what a side option is built from. */
export interface CatalogItem {
  id: string;
  name: string | null;
  brand: string | null;
  description: string | null;
  itemImage: string | null;
}

export function useCatalogSearch() {
  return useMutation({
    mutationFn: (query: string) =>
      unwrap<{ items: CatalogItem[]; count: number }>(
        api.post('businesses/catalog/search', {
          Query: query,
          Page: 0,
          Size: 20,
          Filters: [],
          Sort: 0,
        })
      ),
  });
}
